// Core
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Libraries
import {
  isChrome,
  isFirefox,
  browserName,
  isEdge,
} from 'react-device-detect';
import moment from 'moment';
import 'moment/locale/es';

// Plugins
import {
  configureAws,
  useInterval,
} from '../plugins/general';
import { requestVantageApi } from '../plugins/request';
import { langMap, fallbackLanguage } from '../i18n/i18n';

// Store
import { setApplication, setLanguage, setUnitSystem } from '../store/app/actions';
import { refreshCurrentCustomerEntitlements } from '../store/session/actions';

const ENTITLEMENTS_POLL_INTERVAL_MS = 60000;

const AppController = (props) => {
  const {
    unitSystem,
    language,
    setApplicationAction,
    setUnitSystemAction,
    setLanguageAction,
    refreshCustomerEntitlementsAction,
    children,
    user,
    customer,
  } = props;

  /* ***********************************************************************************************
   ********************************************** LOCAL STATE *************************************
   ********************************************************************************************** */

  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lang');
  const { i18n } = useTranslation();
  const history = useHistory();
  const isChromeHeadless = browserName === 'Chrome Headless';

  /* ***********************************************************************************************
   ********************************************** LOCAL STATE *************************************
   ********************************************************************************************** */

  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [lastUrl, setLastUrl] = useState(null);

  /* ***********************************************************************************************
   ********************************************** METHODS *******************************************
   ********************************************************************************************** */

  const fetchAppInformation = () => {
    requestVantageApi.application('get', `/${process.env.REACT_APP_ID}`, null, true, false)
      .then((response) => {
        setApplicationAction(response.data);
        setMaintenanceMode(response.data.maintenanceMode);
      });
  };

  const setAppLanguage = () => {
    let selectedLanguage;

    if (urlLang) {
      selectedLanguage = urlLang;
    } else if (user && user.mgnt && user.mgnt.lang) {
      selectedLanguage = user.mgnt.lang;
    } else if (language) {
      selectedLanguage = language;
    } else {
      const browserLanguages = [];

      // Get browser langs
      if (navigator.language) browserLanguages.push(navigator.language);
      if (navigator.languages && navigator.languages.length) {
        navigator.languages.forEach((lang) => browserLanguages.push(lang));
      }

      // Verify lang by lang & store first valid
      if (browserLanguages.length) {
        for (let i = 0; i < browserLanguages.length; i += 1) {
          const lang = browserLanguages[i];
          if (lang) {
            const normalizedLang = browserLanguages[i].replace('-', '_').toLowerCase();
            if (langMap[normalizedLang]) {
              selectedLanguage = langMap[normalizedLang];
              break;
            }
          }
        }
      }
    }

    if (selectedLanguage) {
      moment.locale(selectedLanguage);
      i18n.changeLanguage(selectedLanguage);
      setLanguageAction(selectedLanguage);
    } else {
      // keeps fallback lang
      moment.locale(fallbackLanguage);
    }
  };

  const setAppUnitSystem = () => {
    if (!unitSystem) {
      if (language === 'en_US') setUnitSystemAction('imperial');
      else setUnitSystemAction('metric');
    }
  };

  const sendLanguageToApi = () => {
    requestVantageApi.child(
      customer.api.user,
      'put',
      `/${user.sub}`,
      {
        lang: language,
      },
      false,
    );
  };

  /* ***********************************************************************************************
   ***************************************** COMPONENT HANDLING ************************************
   ********************************************************************************************** */

  useEffect(() => {
    configureAws();
    fetchAppInformation();
    setAppUnitSystem();
  }, []);

  useEffect(() => {
    setAppLanguage();
    if (Object.keys(user).length && user['custom:isSuperior'] === '0' && customer) {
      sendLanguageToApi();
    }
  }, [user, language]);

  useInterval(() => {
    fetchAppInformation();
  }, 300000);

  // Soft-refresh entitlements while a customer session is active.
  // Skips hidden tabs; Redux only updates when flags actually change.
  useInterval(() => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (!customer?.api?.user) {
      return;
    }

    refreshCustomerEntitlementsAction();
  }, ENTITLEMENTS_POLL_INTERVAL_MS);

  useEffect(() => {
    const refreshEntitlementsIfVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (!customer?.api?.user) {
        return;
      }

      refreshCustomerEntitlementsAction();
    };

    document.addEventListener('visibilitychange', refreshEntitlementsIfVisible);
    window.addEventListener('focus', refreshEntitlementsIfVisible);

    return () => {
      document.removeEventListener('visibilitychange', refreshEntitlementsIfVisible);
      window.removeEventListener('focus', refreshEntitlementsIfVisible);
    };
  }, [customer, refreshCustomerEntitlementsAction]);

  if (!isChrome && !isFirefox && !isChromeHeadless && !isEdge) {
    history.push('/unsupported-browser');
  } else if (maintenanceMode) {
    if (!history.location.pathname.includes('/maintenance')) {
      setLastUrl(history.location.pathname);
      history.push('/maintenance');
    }
  } else if (!maintenanceMode) {
    if (history.location.pathname.includes('/maintenance')) {
      window.location.href = lastUrl;
    }
  }

  return (
    <>
      {children}
    </>
  );
};

AppController.propTypes = {
  unitSystem: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  customer: PropTypes.object,
  setApplicationAction: PropTypes.func.isRequired,
  setUnitSystemAction: PropTypes.func.isRequired,
  setLanguageAction: PropTypes.func.isRequired,
  refreshCustomerEntitlementsAction: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

AppController.defaultProps = {
  customer: null,
};

// Map state
const mapStateToProps = (state) => ({
  unitSystem: state.app.unitSystem,
  language: state.app.language,
  user: state.auth.user,
  customer: state.session.customer,
});

const mapDispatchToProps = (dispatch) => ({
  setApplicationAction: (app) => dispatch(setApplication(app)),
  setUnitSystemAction: (unitSystem) => dispatch(setUnitSystem(unitSystem)),
  setLanguageAction: (language) => dispatch(setLanguage(language)),
  refreshCustomerEntitlementsAction: () => dispatch(refreshCurrentCustomerEntitlements()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppController);
